import { Controller } from '@hotwired/stimulus'
import toastr from '../src/init/toastr'
import { RoipClient, guardFocus, AudioMixer } from '@sphere-drones-internal/roip-client'

const ROIP_URL = 'wss://staging.roip.curoedge.com/v1/ws'

export default class extends Controller {
  static targets = [
    'roipBrokerGrantInput',
    'mediaModeInput',
    'connectionStatus',
    'helloOutput',
    'radioInput',
    'listenButton',
    'pipeStatus',
    'pipeReasonStatus',
    'squelchStatus',
    'frequencyInput',
    'frequencyStatus',
    'volumeInput',
    'micCheckbox',
    'pttButton',
    'pttStatus',
    'micStatus'
  ]

  static values = {
    accessToken: String,
    radioGrantUrl: String
  }

  connect () {
    this.client = null
    this.audioContext = null
    this.mixer = null
    this.AudioMixer = null
    this.heartbeatTimer = null
    this.activeRadioId = null
    this.subscribedRadioId = null
    this._micRequestId = 0
    this._listenRequestId = 0
    this._pttUp = () => this.pttUp()
    window.addEventListener('pointerup', this._pttUp)
  }

  disconnect () {
    this.disconnectFromMimerRoip()
    window.removeEventListener('pointerup', this._pttUp)
  }

  startHeartbeat () {
    this.stopHeartbeat()
    this.heartbeatTimer = setInterval(() => {
      this.client?.ping()
    }, 250)
  }

  stopHeartbeat () {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  get roipBrokerGrant () {
    return this.hasRoipBrokerGrantInputTarget ? this.roipBrokerGrantInputTarget.value.trim() : ''
  }

  get mediaMode () {
    return this.hasMediaModeInputTarget ? this.mediaModeInputTarget.value : 'auto'
  }

  get radioId () {
    return this.hasRadioInputTarget ? this.radioInputTarget.value.trim() : ''
  }

  async requestGrant () {
    try {
      const response = await fetch(this.radioGrantUrlValue, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${this.accessTokenValue}`
        }
      })

      const data = await response.json()

      if (!response.ok) {
        const error = data.errors?.[0]
        toastr.error(error?.detail || 'Unable to request a radio grant', error?.title)
        return
      }

      this.roipBrokerGrantInputTarget.value = data.radio_grant.grant
      toastr.success('Radio grant received')
    } catch (error) {
      toastr.error('There was an error requesting the radio grant.')
    }
  }

  async connectToMimerRoip () {
    if (this.client) {
      toastr.error('Already connected')
      return
    }

    if (!this.roipBrokerGrant) {
      toastr.error('ROIP_BROKER_GRANT is required')
      return
    }

    this.AudioMixer = AudioMixer

    const client = new RoipClient(ROIP_URL, {
      token: this.roipBrokerGrant,
      media: this.mediaMode
    })
    guardFocus(client)

    this.client = client

    client.on('hello', (hello) => {
      this.connectionStatusTarget.textContent = 'Connected'
      this.helloOutputTarget.textContent = JSON.stringify(hello, null, 2)
    })

    client.on('track', ({ radio, stream }) => {
      if (radio !== this.subscribedRadioId || !this.mixer) {
        return
      }

      // Both pipes route through the same AudioMixer graph, so this replaces the floor's
      // source node in place rather than opening a second, competing playback path.
      this.mixer.attachStream(radio, stream)
      this.pipeStatusTarget.textContent = 'webrtc/opus'
    })

    client.on('audio', ({ radio, samples }) => {
      if (radio !== this.subscribedRadioId || !this.mixer) {
        return
      }

      this.mixer.playFloor(radio, samples)
      this.pipeStatusTarget.textContent = 'websocket/ulaw'
    })

    client.on('error', (event) => {
      toastr.error(`RoIP error (${event.code}): ${event.message}`)
    })

    client.on('state', (state) => {
      if (state.radio !== this.subscribedRadioId) {
        return
      }

      // Squelch is step-only (no typed input to reconcile), so just reflect the radio's own reading.
      if (state.squelch_detent && typeof state.squelch_detent.value === 'number') {
        if (this.hasSquelchStatusTarget) {
          this.squelchStatusTarget.textContent = state.squelch_detent.value + (state.squelch_detent.confirmed ? '' : ' (commanded)')
        }
      }

      if (state.frequency_mhz && typeof state.frequency_mhz.value === 'number') {
        if (this.hasFrequencyStatusTarget) {
          this.frequencyStatusTarget.textContent = state.frequency_mhz.value.toFixed(3) + (state.frequency_mhz.confirmed ? '' : ' (commanded)')
        }
      }
    })

    client.on('ptt', (event) => {
      if (event.radio && event.radio !== this.activeRadioId) {
        return
      }

      this.pttStatusTarget.textContent = event.event + (event.reason ? ` (${event.reason})` : '')
      if (event.event === 'ptt_revoked') {
        toastr.warning(`Transmission revoked: ${event.reason}`)
      }
      this.refreshPtt()
    })

    client.on('close', () => {
      if (this.client === client) {
        this.client = null
        this.disconnectFromMimerRoip()
        this.connectionStatusTarget.textContent = 'Disconnected'
      }
    })

    try {
      await client.connect()

      if (this.client !== client) {
        client.close()
        return
      }

      this.startHeartbeat()
      toastr.success('Connected')
    } catch (error) {
      if (this.client === client) {
        this.client = null
      }
      toastr.error('Unable to connect')
      client.close()
    }
  }

  async listen () {
    if (!this.client) {
      toastr.error('Connect before listening')
      return
    }

    if (!this.radioId) {
      toastr.error('Radio ID is required')
      return
    }

    const client = this.client
    const radioId = this.radioId
    const requestId = ++this._listenRequestId

    // Stable for the duration of this attempt: media events and mixer gain must not follow
    // the live textbox if the operator edits it while this subscription is still pending.
    this.subscribedRadioId = radioId
    this.activeRadioId = null
    this.refreshPtt()

    this.audioContext = this.audioContext || new window.AudioContext()
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume()
    }
    this.mixer = this.mixer || new this.AudioMixer(this.audioContext)

    try {
      const outcome = await client.subscribeWithMedia(radioId)

      if (this.client !== client || requestId !== this._listenRequestId) {
        // Superseded by a disconnect/reconnect, or by a newer Listen click for a different
        // radio — applying this now would overwrite that newer selection.
        return
      }

      this.pipeStatusTarget.textContent = outcome.pipe === 'webrtc' ? 'webrtc/opus' : 'websocket/ulaw'
      // reason is the only field that says why negotiation fell back to the floor
      if (this.hasPipeReasonStatusTarget) {
        this.pipeReasonStatusTarget.textContent = outcome.reason || 'no reason'
      }
      client.select(radioId)
      this.activeRadioId = radioId
      this.refreshPtt()
    } catch (error) {
      if (requestId === this._listenRequestId) {
        toastr.error('Unable to subscribe to radio')
      }
    }
  }

  adjustSquelch (event) {
    if (!this.client) {
      toastr.error('Connect before adjusting squelch')
      return
    }

    if (!this.radioId) {
      toastr.error('Radio ID is required')
      return
    }

    // The radio has no set-level command, only relative steps, so each press sends exactly one.
    this.client.squelch(this.radioId, event.params.direction)
  }

  tuneFrequency () {
    if (!this.client) {
      toastr.error('Connect before tuning')
      return
    }

    if (!this.radioId) {
      toastr.error('Radio ID is required')
      return
    }

    const raw = this.frequencyInputTarget.value.trim()
    if (!/^\d{1,3}\.\d{3}$/.test(raw)) {
      toastr.error('Frequency must be formatted as xxx.xxx')
      return
    }

    const hz = Math.round(parseFloat(raw) * 1e6)
    this.client.tune(this.radioId, hz)
  }

  setVolume () {
    if (!this.mixer || !this.subscribedRadioId) {
      return
    }

    const value = parseFloat(this.volumeInputTarget.value)
    if (Number.isNaN(value)) {
      return
    }

    this.mixer.setRadioGain(this.subscribedRadioId, value)
  }

  async toggleMicrophone () {
    if (!this.client) {
      toastr.error('Connect before enabling the microphone')
      this.micCheckboxTarget.checked = false
      return
    }

    const client = this.client
    const requestId = ++this._micRequestId

    if (!this.micCheckboxTarget.checked) {
      try {
        await client.setMicrophone(null)
      } catch (error) {
        // The SDK may still consider itself armed; don't show the mic as released if we
        // can't confirm the release actually happened.
        if (requestId === this._micRequestId && this.client === client) {
          this.micCheckboxTarget.checked = true
          toastr.error('Unable to release the microphone')
        }
      }
      if (requestId === this._micRequestId && this.client === client) {
        this.refreshPtt()
      }
      return
    }

    let stream = null
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          autoGainControl: false,
          echoCancellation: false,
          noiseSuppression: false,
          sampleRate: 8000,
          channelCount: 1
        },
        video: false
      })

      // A later uncheck or reconnect may already have settled while getUserMedia was
      // pending; arming this stale request would re-enable audio the operator turned off.
      if (requestId !== this._micRequestId || this.client !== client) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      await client.setMicrophone(stream)

      if (requestId !== this._micRequestId || this.client !== client) {
        stream.getTracks().forEach((track) => track.stop())
        await client.setMicrophone(null)
        return
      }

      toastr.success('Microphone armed — audio stays muted until the radio confirms transmitting')
    } catch (error) {
      stream?.getTracks().forEach((track) => track.stop())
      if (requestId === this._micRequestId && this.client === client) {
        this.micCheckboxTarget.checked = false
        toastr.error('Unable to open the microphone')
      }
    }

    if (requestId === this._micRequestId && this.client === client) {
      this.refreshPtt()
    }
  }

  refreshPtt () {
    if (!this.hasPttButtonTarget) {
      return
    }

    const stats = this.client?.micStats?.() || { armed: false, pipe: null }
    const ready = Boolean(this.client) && Boolean(this.activeRadioId) && stats.armed
    this.pttButtonTarget.disabled = !ready
    this.pttButtonTarget.title = !this.client
      ? 'connect first'
      : !this.activeRadioId
          ? 'listen to a radio first'
          : !stats.armed
              ? 'no microphone armed — keying now would put an unmodulated carrier on air'
              : `hold to transmit on ${this.activeRadioId} (${stats.pipe})`
    if (this.hasMicStatusTarget) {
      this.micStatusTarget.textContent = stats.armed ? `armed (${stats.pipe})` : 'not armed'
    }
  }

  pttDown (event) {
    if (event?.repeat) {
      return
    }
    event?.preventDefault()
    if (!this.client || this.pttButtonTarget.disabled) {
      return
    }

    try {
      this.pttButtonTarget.setPointerCapture?.(event?.pointerId)
    } catch { /* not all pointers capture */ }

    const keyed = this.client.pttDown(this.activeRadioId)
    if (keyed === false) {
      toastr.error('Microphone is not ready yet')
    }
  }

  pttUp (event) {
    event?.preventDefault()
    if (this.client?.keyed) {
      this.client.pttUp()
    }
  }

  disconnectFromMimerRoip () {
    this.stopHeartbeat()
    this.activeRadioId = null
    this.subscribedRadioId = null

    if (this.client) {
      this.client.close()
      this.client = null
    }

    if (this.mixer) {
      this.mixer.close()
      this.mixer = null
    }

    if (this.audioContext) {
      this.audioContext.close()
      this.audioContext = null
    }

    this.connectionStatusTarget.textContent = 'Unknown'
    this.helloOutputTarget.textContent = ''
    this.pipeStatusTarget.textContent = 'Unknown'
    if (this.hasPipeReasonStatusTarget) this.pipeReasonStatusTarget.textContent = 'Unknown'
    if (this.hasSquelchStatusTarget) this.squelchStatusTarget.textContent = 'Unknown'
    if (this.hasFrequencyStatusTarget) this.frequencyStatusTarget.textContent = 'Unknown'
    if (this.hasPttStatusTarget) this.pttStatusTarget.textContent = 'idle'
    if (this.hasMicStatusTarget) this.micStatusTarget.textContent = 'not armed'
    if (this.hasMicCheckboxTarget) this.micCheckboxTarget.checked = false
    this.refreshPtt()
  }
}
